import * as dayjs from 'dayjs';
import { ReportDisplayComponent } from './../../../reports/components/display/report.display.component';
import { faUsers, faHeart, faClipboard, faPlus, faCheck, faUpload, faUmbrella, faStaffSnake, faLungs, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import { OnChanges, SimpleChanges } from '@angular/core';
// ng
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router, ActivatedRoute, RouterEvent, NavigationStart, NavigationEnd } from '@angular/router';

// ext
import { map, maxBy, filter, get, find, cloneDeep } from 'lodash';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

// local
import SystemUser from 'src/app/models/user';
import { AccessLevel, CoreService } from 'src/app/services/core.service';
import { AuthService } from 'src/app/services/auth.service';
import Subscriber from 'src/app/models/subscriber';
import EnrollmentPeriod from 'src/app/models/enrollmentPeriod';
import Milestone from '../../../../models/milestone';
import { sortBy } from 'lodash';
import { env } from 'src/env/development';
import SubscriberEffectiveSummary from 'src/app/models/subscriberEffectiveSummary';
import Member from 'src/app/models/member';
import MemberSummary from 'src/app/models/memberSummary';
import { WizardRefreshService } from 'src/app/services/wizardRefresh.service';
import WizardRefreshRequest from 'src/app/models/wizardRefreshRequest';
import Plan from 'src/app/models/plan';

@UntilDestroy()
@Component({
  selector: 'pebb-wizard',
  templateUrl: 'wizard.component.html',
  styles: [

  ],
  providers: [],
})
export class WizardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() subscriber: Subscriber;
  @Input() isCompleteOrOptedOut ? = false;
  @Input() isSOE = false;
  @Input() enrollmentPeriod: EnrollmentPeriod;
  @Input() modifiedMilestoneNameOrder: string[] = null;
  @Input() enabledMilestoneSteps: string[] = null;
  authService: AuthService;
  systemUser: SystemUser;
  selectedEnrollmentPeriod: EnrollmentPeriod;
  formattedMilestones = [];
  current = 0;
  maxMilestoneComplete;
  isReadOnly = false;
  filteredUpload = false;
  wizardRoutingSubscription: Subscription;
  wizardRefreshSubscription: Subscription;
  wizardNextPageSubscription: Subscription;
  icons = {
    faUsers,
    faHeart,
    faClipboard,
    faCheck,
    faPlus,
    faUpload,
    faUmbrella,
    faStaffSnake,
    faLungs,
    faCircleExclamation
  };
  filteredTobacco: boolean = false;

  constructor(private coreService: CoreService, private route: ActivatedRoute, private router: Router, private refreshService: WizardRefreshService) {}

  ngOnInit(): void {
    this.wizardRoutingSubscription = this.router.events.pipe(untilDestroyed(this)).subscribe((e: RouterEvent) => {
      if (e instanceof NavigationEnd) {
        this.setFormattedMilestones(this.subscriber);
      }
    });
    this.isReadOnly = this.coreService.systemUserHasAccess(AccessLevel.ReadOnly) && !this.coreService.systemUserHasAccess(AccessLevel.Edit);

    this.setFormattedMilestones(this.subscriber);

    this.wizardRefreshSubscription = this.refreshService.refreshRequest.pipe(untilDestroyed(this))
      .subscribe((data: WizardRefreshRequest) => 
        this.setFormattedMilestones(data.subscriber, data.planSelections)
      );

    this.wizardNextPageSubscription = this.refreshService.nextPage.pipe(untilDestroyed(this))
      .subscribe((enrollmentPeriod: EnrollmentPeriod) =>
        this.gotoNextPage(enrollmentPeriod)
      );
  }

  gotoPreviousPage(enrollmentPeriod: EnrollmentPeriod): void {
    if (this.current === 0)
      return;

    const previousEpsThatWereWorkedOnStep = this.formattedMilestones[--this.current];
    const link = this.getLink(previousEpsThatWereWorkedOnStep);

    this.router.navigate([link], { relativeTo: this.route });
  }

  gotoNextPage(enrollmentPeriod: EnrollmentPeriod): void {
    if (this.current === this.formattedMilestones.length - 1)
      return;

    const nextStep = this.formattedMilestones[++this.current];
    const link = this.getLink(nextStep);

    this.router.navigate([link], { relativeTo: this.route });
  }

  getLink(step): string {
    return this.isSOE ? `./${this.subscriber.memberId}/${step.state}` : `./${step.state}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // This will trigger most likely due to a change to the subscriber object
    this.setFormattedMilestones(changes?.subscriber?.currentValue ?? this.subscriber);
  }

  ngOnDestroy(): void {}

  activateStep(e): void {
    this.setFormattedMilestones(this.subscriber);
  }

  getMaxMilestoneCompleted(milestones: Milestone[]): Milestone {
    return (
      maxBy(
        filter(milestones, (mi: Milestone) => mi.isComplete),
        'sortOrder'
      ) || sortBy(this.selectedEnrollmentPeriod.milestones, 'sortOrder')[0]
    );
  }

  optOut(bool: boolean): void {
    this.isCompleteOrOptedOut = bool || this.getMaxMilestoneCompleted(this.selectedEnrollmentPeriod.milestones).milestoneName === 'Confirmation';
  }

  setFormattedMilestones(subscriber: Subscriber, planSelections: { [k: string]: Plan } = null): void {
    // this.selectedEnrollmentPeriod should be based on the subscriber's enrollmentPeriods since these will change when there are changes to milestone completions
    // this.enrollmentPeriod will not since it is a copy of one of the subscriber's enrollment periods.
    this.selectedEnrollmentPeriod = cloneDeep(subscriber.enrollmentPeriods.find(o => o.enrollmentPeriodId === this.enrollmentPeriod.enrollmentPeriodId));
    if (!this.selectedEnrollmentPeriod) {
      this.selectedEnrollmentPeriod = this.enrollmentPeriod;
    }

    if (this.modifiedMilestoneNameOrder) {
      this.selectedEnrollmentPeriod.milestones.forEach((m: Milestone) => m.sortOrder = this.modifiedMilestoneNameOrder.indexOf(m.milestoneName));
    }

    this.maxMilestoneComplete = this.getMaxMilestoneCompleted(this.selectedEnrollmentPeriod.milestones);
    this.formattedMilestones = map(sortBy(this.selectedEnrollmentPeriod.milestones.filter(mi => env.milestoneMap[mi.milestoneName]), 'sortOrder'), (mi: Milestone) => ({
      id: mi.milestoneId,
      label: mi.milestoneName,
      icon: env.milestoneMap[mi.milestoneName].icon,
      state: env.milestoneMap[mi.milestoneName].state + this.selectedEnrollmentPeriod.enrollmentPeriodId,
      disabled: ((mi.sortOrder > this.maxMilestoneComplete.sortOrder && !mi.completedDate) || this.isReadOnly),
      sort: mi.sortOrder,
    }));

    this.filteredUpload = this.filterUploadMilestone();
    this.filteredTobacco = this.filterTobaccoMilestone(subscriber, planSelections);

    if (this.filteredUpload) {
      this.formattedMilestones = this.formattedMilestones.filter(mi => mi.label !== 'Upload');
    }

    if (this.filteredTobacco) {
      this.formattedMilestones = this.formattedMilestones.filter(mi => mi.label !== 'Attestations');
    }

    if (this.enrollmentPeriod.enrollmentPeriodType.enrollmentPeriodTypeCode !== 'OE') {
      // Spousal re-attest is only applicable during OE
      this.formattedMilestones = this.formattedMilestones.filter(mi => mi.label !== 'Spousal Attestation');
    } else {
      // US 42241
      if (!this.subscriber.mustReattestSpousal(this.enrollmentPeriod, true, true))
        this.formattedMilestones = this.formattedMilestones.filter(mi => mi.label !== 'Spousal Attestation');
    }

    this.isCompleteOrOptedOut = this.maxMilestoneComplete.milestoneName === 'Confirmation';

    const selStep = this.formattedMilestones.find(o => this.router.url.indexOf(o.state) > -1);
    if (selStep) {
      this.current = this.formattedMilestones.indexOf(selStep);
      if (!this.isReadOnly) {
        // enable the current step and any previous steps
        selStep.disabled = false;
        this.formattedMilestones.filter(o => o.sort < selStep.sort).forEach(step => {
          step.disabled = false;
        });
      }
    }

    if (this.enabledMilestoneSteps?.length && this.enabledMilestoneSteps[0] != 'All') {
      this.formattedMilestones.forEach(m => {
        if (!m.disabled && this.enabledMilestoneSteps.indexOf(m.milestoneName) === -1) {
           m.disabled = true;
        }
      });
    }

    this.refreshService.formattedMilestones = cloneDeep(this.formattedMilestones);
  }

  // Check if the upload milestone needs to be removed
  // returns: true if the milestone should be removed from the wizard
  filterUploadMilestone() : boolean {
    if (this.enrollmentPeriod.enrollmentPeriodType?.enrollmentPeriodTypeCode !== 'OE')
      return false;

    return !this.checkForDependentChange();
  }

  // filter the tobacco milestone for OE and SOE
  filterTobaccoMilestone(subscriber: Subscriber, planSelections: { [k: string]: Plan }) : boolean {
    if (!(this.enrollmentPeriod?.enrollmentPeriodType?.enrollmentPeriodTypeCode === 'OE'))
      return false;

    return !this.checkReturnFromWaivedMedical(subscriber, planSelections);
  }

  checkReturnFromWaivedMedical(subscriber: Subscriber, planSelections: { [k: string]: Plan }) : boolean {
    let subscriberReturn = false;
    const subSummary = new SubscriberEffectiveSummary(subscriber, this.enrollmentPeriod, false);
    subSummary.members.some(sub => {
      if (sub.isSubscriberInd) {
        // check if the subscriber has active medical coverage or if they have a plan that ends the day before with a new plan the day of the enrollment period (no break in coverage)
        // Since this is only applying to the OE or SOE wizard, we dont need to check that they are a fresh subscriber as that logic should be handled in the NE wizard
        const medicalEnrollments = sub.enrollments.filter(e => e.plan.planType.planTypeName === 'Medical');
        const dayBeforeOE = dayjs(this.enrollmentPeriod.coverageEffectiveStartDate).subtract(1, 'day').toDate();
        const hasPreviousMedicalCoverage = medicalEnrollments.find(e => e.isActive(dayBeforeOE)) !== undefined;
        let hasCurrentMedicalCoverage = medicalEnrollments.find(e => e.isActive(this.enrollmentPeriod.coverageEffectiveStartDate)) !== undefined;

        if (planSelections) {
          // Unsaved data passed in from UI to consider (for current enrollment period selection).
          hasCurrentMedicalCoverage = planSelections.medical !== null;
        }

        if (!hasPreviousMedicalCoverage && hasCurrentMedicalCoverage) {
          subscriberReturn = true;
        }
      }
    });
    return subscriberReturn;
  }

  // Check if, for the current enrollment period, dependents had the following changes:
  // a new dependent is added
  // a subscriber adds coverage to a dependent who did not have previous coverage
  // returns: true if there was a dependent change that would require the wizard to be used
  checkForDependentChange() : boolean {
    if (this.subscriber.members?.length === 1) return false;
    var dependentChange = false;
    var subSummary = new SubscriberEffectiveSummary(this.subscriber, this.enrollmentPeriod, false);
    subSummary.members.some(dep => {
      if (!dep.isSubscriberInd) {
        if (dayjs(dep.relationship.effectiveStartDate).isSameOrAfter(this.enrollmentPeriod.effectiveStartDate)) {
           // new relationship added
          dependentChange = true;
        } else {
          // previous relationship
          // dependent has new coverage for this enrollment period but was previous waived (has a plan that was end dated one day before the enrollment period)
          dependentChange = this.hasNewCoverageAdded(dep) && !this.hasChangedPlans(dep);
        }
        if (dependentChange) return true; // exit out of some() block
      }
    });
    return dependentChange;
  }

  // coverage added after not being enrolled, no plan exists before the enrollment period and new plan exists for the enrollment period
  hasNewCoverageAdded(dep: Member): boolean {
    // they would not have active coverage if there was not an enrollment before the enrollment period started that was 1. active or 2. end dated day before (they are changing plans)
    var hadNoActiveCoverage = dep.enrollments.find(e => dayjs(e.effectiveStartDate).isBefore(this.enrollmentPeriod.coverageEffectiveStartDate) && (e.effectiveEndDate == null)) === undefined;
    var hasCurrMedicalCoverage = dep.enrollments.find(e => e.plan.planType.planTypeName === 'Medical' && dayjs(e.effectiveStartDate).isSame(this.enrollmentPeriod.coverageEffectiveStartDate)) !== undefined;
    var hasCurrDentalCoverage = dep.enrollments.find(e => e.plan.planType.planTypeName === 'Dental' && dayjs(e.effectiveStartDate ).isSame(this.enrollmentPeriod.coverageEffectiveStartDate)) !== undefined;
    var hasCurrVisionCoverage = dep.enrollments.find(e => e.plan.planType.planTypeName === 'Vision' && dayjs(e.effectiveStartDate).isSame(this.enrollmentPeriod.coverageEffectiveStartDate)) !== undefined;
    return hadNoActiveCoverage &&  (hasCurrMedicalCoverage || hasCurrDentalCoverage || hasCurrVisionCoverage);
  }

  // in the case when a dependent changes their coverages, the previous plan will be ended prior to the enrollment period with a new plan
  // we do not want to consider this as new coverage added
  // returns: true if there was a plan change
  hasChangedPlans(dep: Member): boolean
  {
    var allPlansEndedBeforeEP = dep.enrollments.filter(e => e.effectiveEndDate != null && dayjs(e.effectiveEndDate).isSame(dayjs(this.enrollmentPeriod.coverageEffectiveStartDate).subtract(1, 'day').startOf('day').toDate()));
    var allPlansNewForEP = dep.enrollments.filter(e => e.effectiveEndDate == null && dayjs(e.effectiveStartDate).isSame(this.enrollmentPeriod.coverageEffectiveStartDate));
    var hadPlanChange = false;
    allPlansEndedBeforeEP.some(e => {
      var matchingPlan = allPlansNewForEP.find(e2 => e2.planTypeId === e.planTypeId) !== undefined;
      if (matchingPlan) {
        hadPlanChange = true;
        return true; // exit out of some() block
      } 
    });
    return hadPlanChange;
  }
}